import { createSelector } from "@reduxjs/toolkit";
import { GetMcbimSpacesApiResponse, openapi } from "../../openapi";
import { IRootState } from "../app/store";
import { selectedRoomNr } from "./McBimSlice";
import { selectedSpaceNumber } from "../DesignTab/designSelectors";

import { selectMcBimItemsForViewer } from "./mcbimItemSelectors";
import { getHeightFromFirstCeilingGrid } from "../DesignTab/designSlice";
import { ICeilingGridItem } from "@multiconsult-group/multiplacer-core";
import { selectHttpMcbimSpacesResult } from "./httpMcBimSelectors";
import { selectProjectNo } from "../globalSelectors";

// Separate empty responses to avoid union types

const EMPTY_SPACES: GetMcbimSpacesApiResponse = [];

// ----- Basic McBIM selectors -----

export const selectNrOfMcbimItems = createSelector([selectMcBimItemsForViewer], (bimItems) => {
  return bimItems.length;
});

export const selectnrOfMcbimSpaces = createSelector([selectHttpMcbimSpacesResult], (res) => {
  return res.length;
});

export const selectActivePartialMcbimSpace = createSelector(
  [selectedSpaceNumber, selectHttpMcbimSpacesResult],
  (selectedNumber, mcbimSpaces) => {
    if (!selectedNumber) return null;
    return mcbimSpaces.find((space) => space.name === selectedNumber) ?? null;
  }
);

export const selectGetActiveFullMcbimSpaceArgs = createSelector(
  [selectedSpaceNumber, selectProjectNo],
  (selectedNumber, projectNo) => {
    if (!selectedNumber || !projectNo) {
      return null;
    }

    return {
      projectNo,
      name: selectedNumber,
    };
  }
);

export const selectHttpActiveFullMcbimSpace = createSelector(
  [selectGetActiveFullMcbimSpaceArgs, (state: IRootState) => state],
  (args, state) => {
    if (!args) {
      return null;
    }

    return openapi.endpoints.getMcbimSingleMcbimSpace.select(args)(state).data ?? null;
  }
);

export const selectMcBimSpaceFromSelectedRoomNr = createSelector(
  [selectedRoomNr, selectHttpMcbimSpacesResult],
  (selectedRoomNr, mcbimSpaces) => {
    return mcbimSpaces.find((space) => space.name === selectedRoomNr);
  }
);

export const makeSelectCeilingGridHeightMmByRoomNumber = () =>
  createSelector(
    [selectHttpMcbimSpacesResult, (_state: IRootState, roomNumber: string) => roomNumber],
    (spaces, roomNumber): number | undefined => {
      const space = spaces.find((s) => s?.name === roomNumber);
      const ceilingGrids: ICeilingGridItem[] = space?.ceilingGrids ?? [];
      return getHeightFromFirstCeilingGrid(ceilingGrids);
    }
  );

export const selectActiveCeilingGridHeightMm = createSelector(
  [selectActivePartialMcbimSpace],
  (activeSpace): number | undefined => {
    const ceilingGrids: ICeilingGridItem[] = activeSpace?.ceilingGrids ?? [];
    return getHeightFromFirstCeilingGrid(ceilingGrids);
  }
);

export const selectActiveCeilingHeightAboveFloorMm = createSelector(
  [selectActivePartialMcbimSpace],
  (activeSpace): number | undefined => {
    const ceilingGrids: ICeilingGridItem[] = activeSpace?.ceilingGrids ?? [];
    return getHeightFromFirstCeilingGrid(ceilingGrids);
  }
);
